/**
 * Convert a rangeId from the topbar pill into concrete IST `from` / `to`
 * date strings that the backend handlers consume as query params.
 *
 *   resolveRange('this-month')       → { from: '2026-04-01', to: '2026-04-28' }
 *   resolveRange('today')            → { from: '2026-04-28', to: '2026-04-28' }
 *   resolveRange('till-date')        → { from: null, to: '2026-04-28' }   // backend uses earliest available
 *   resolveRange('custom', { from, to }) → { from, to }
 */

function ymd(d) {
  // IST date — "en-CA" outputs YYYY-MM-DD by default.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
}

function startOfMonth(d) {
  const ist = ymd(d);
  return ist.slice(0, 7) + "-01";
}
function startOfYear(d) {
  return ymd(d).slice(0, 4) + "-01-01";
}
function shiftMonth(yyyymmdd, delta) {
  // yyyymmdd is YYYY-MM-DD; shift by N months, snap to first-of-month.
  const [y, m] = yyyymmdd.split("-").map(Number);
  const total = (y * 12 + (m - 1)) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, "0")}-01`;
}
function endOfMonth(yyyymmdd) {
  const next = shiftMonth(yyyymmdd, 1);
  const d = new Date(next);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function resolveRange(rangeId, customRange) {
  const now = new Date();
  const today = ymd(now);

  switch (rangeId) {
    case "today":
      return { from: today, to: today };
    case "this-week": {
      // ISO week: Monday → today
      const d = new Date(now);
      const day = d.getDay() || 7;       // Sun=0 → 7
      const monday = new Date(d.getTime() - (day - 1) * 86400000);
      return { from: ymd(monday), to: today };
    }
    case "this-month":
      return { from: startOfMonth(now), to: today };
    case "last-month": {
      const thisMonth = startOfMonth(now);
      const last = shiftMonth(thisMonth, -1);
      return { from: last, to: endOfMonth(last) };
    }
    case "this-year":
      return { from: startOfYear(now), to: today };
    case "last-year": {
      const y = Number(today.slice(0, 4)) - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
    case "till-date":
      // Lifetime — explicit null `from` tells the backend "no lower bound"
      return { from: null, to: today };
    case "custom":
      if (customRange && customRange.from && customRange.to) {
        return { from: customRange.from, to: customRange.to };
      }
      return { from: today, to: today };
    default:
      return { from: startOfMonth(now), to: today };
  }
}

/** Display-friendly label for the active range. */
export function rangeLabel(rangeId, customRange) {
  if (rangeId === "custom" && customRange && customRange.from && customRange.to) {
    return `${customRange.from} → ${customRange.to}`;
  }
  return ({
    "today":      "today",
    "this-week":  "this week",
    "this-month": "this month",
    "last-month": "last month",
    "this-year":  "this year",
    "last-year":  "last year",
    "till-date":  "lifetime",
    "custom":     "custom range",
  }[rangeId]) || "this month";
}
